import type { Prisma, PrismaClient } from '@prisma/client';
import type { NextFunction, Request, RequestHandler, Response } from 'express';
import { expandPermissions } from '@roovers/shared';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import type { AuthContext } from './auth.types.js';
import { verifyAccessToken } from './tokens.js';

export function permissionsGranted(granted: readonly string[], required: string): boolean {
  return granted.includes('*') || granted.includes(required);
}

/** Loads the user, roles and permissions fresh on every request, so role changes apply at once. */
export async function loadAuthContext(
  db: PrismaClient | Prisma.TransactionClient,
  userId: number,
  companyId: number,
): Promise<AuthContext | null> {
  const user = await db.user.findFirst({
    where: { id: userId, companyId },
    include: { roles: { include: { role: { include: { permissions: true } } } } },
  });
  if (!user || user.status !== 'active') return null;

  const roles = user.roles.map((ur) => ur.role);
  const permissions = new Set<string>();
  for (const role of roles) for (const p of role.permissions) permissions.add(p.permission);
  const allScope = roles.length === 0 || roles.some((r) => r.scope === 'all');
  const limits = roles.map((r) => (r.approvalLimit == null ? null : Number(r.approvalLimit)));

  return {
    userId: user.id,
    companyId: user.companyId,
    email: user.email,
    name: user.name,
    roleIds: roles.map((r) => r.id),
    roleNames: roles.map((r) => r.name),
    permissions: [...permissions],
    scope: allScope ? 'all' : 'own',
    approvalLimit: limits.some((l) => l === null)
      ? null
      : limits.length
        ? Math.max(...(limits as number[]))
        : 0,
    requiresTwoFactor: roles.some((r) => r.requiresTwoFactor),
    twoFactorEnabled: user.twoFactorEnabled,
  };
}

interface AuthDeps {
  db: PrismaClient;
  config: Pick<Config, 'JWT_ACCESS_SECRET'>;
}

/**
 * Requires a valid access token. Users whose role demands 2FA but who have not enrolled are
 * blocked from everything except the routes marked `allowUnenrolled` (2FA setup, /me, logout).
 */
export function authenticate(
  deps: AuthDeps,
  options: { allowUnenrolled?: boolean } = {},
): RequestHandler {
  return async (req: Request, _res: Response, next: NextFunction) => {
    try {
      const header = req.headers.authorization;
      const token = header?.startsWith('Bearer ') ? header.slice(7) : undefined;
      if (!token) throw new HttpError(401, 'UNAUTHENTICATED', 'Sign in required');
      const claims = await verifyAccessToken(deps.config, token);
      if (!claims) throw new HttpError(401, 'UNAUTHENTICATED', 'Session expired');
      const ctx = await loadAuthContext(deps.db, claims.userId, claims.companyId);
      if (!ctx) throw new HttpError(401, 'UNAUTHENTICATED', 'Session expired');
      if (ctx.requiresTwoFactor && !ctx.twoFactorEnabled && !options.allowUnenrolled) {
        throw new HttpError(
          403,
          'TWO_FACTOR_ENROLMENT_REQUIRED',
          'Set up two-factor authentication to continue',
        );
      }
      req.auth = ctx;
      next();
    } catch (err) {
      next(err);
    }
  };
}

/** Deny by default: every business route must name the permission(s) it needs (any one suffices). */
export function requirePermission(...required: string[]): RequestHandler {
  return (req, _res, next) => {
    const auth = req.auth;
    if (!auth) return next(new HttpError(401, 'UNAUTHENTICATED', 'Sign in required'));
    if (required.some((p) => permissionsGranted(auth.permissions, p))) return next();
    next(new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this'));
  };
}

export const can = (auth: AuthContext, permission: string) =>
  permissionsGranted(auth.permissions, permission);

/** Stores expanded permissions for a role, ignoring unknown keys. */
export const normalisePermissions = (patterns: readonly string[]) => expandPermissions(patterns);
